import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemContent, Link, Divider } from '@mui/joy';
import { animate, stagger } from 'motion';
import { useEffect, useRef } from 'react';

const Privacy = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const elements = containerRef.current.querySelectorAll('.animate-in');
        
        animate(
            elements,
            { 
                opacity: [0, 1],
                y: [20, 0]
            },
            { 
                delay: stagger(0.1),
                duration: 0.6,
                easing: 'ease-out'
            }
        );
    }, []);

    return (
        <Box ref={containerRef}>
            <Container sx={{ py: 8 }}>
                <Box className="animate-in" sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        level="h1"
                        fontSize="xl4"
                        sx={{
                            mb: 3,
                            bgcolor: 'text.primary',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Privacy Policy
                    </Typography>
                    <Typography 
                        level="body1" 
                        fontSize="lg" 
                        sx={{ 
                            maxWidth: 800, 
                            mx: 'auto',
                            color: 'text.secondary',
                            lineHeight: 1.6
                        }}
                    >
                        The privacy of your data—and it is your data, not ours!—is a big deal to us. In this policy, we lay out: what data we collect and why; how your data is handled; and your rights with respect to your data. We promise we never sell your data: never have, never will.
                    </Typography>
                </Box>

                <Box className="animate-in" sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        This policy applies to all products built and maintained by Hubitable Ltd. It also applies to our handling of information about site visitors, prospective customers, and customers and authorized users (in relation to their procurement of the services and management of their relationship with Hubitable). We refer collectively to these categories of individuals as "you" throughout this policy.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        However, this policy does not cover information about a customer's end users that Hubitable receives from a customer, or otherwise processes on a customer's behalf, in connection with the services provided by Hubitable to the customer pursuant to an applicable services agreement (including the content of messages of customer end users ("End User Communications")). Hubitable processes End User Communications under the instructions of the relevant customer, which is the "data controller" (or occupies a similar role as defined in applicable privacy laws), as described in the applicable services agreement between such customer and Hubitable. Hubitable's obligations as a "data processor" with respect to such information are defined in such services agreement and applicable data protection addendum and are not made part of this policy.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        If you are a customer's end user and you have questions about how your information is collected and processed through the services, please contact the organization who has provided your information to us for more information.
                    </Typography>

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        What we collect and why
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        Our guiding principle is to collect only what we need. Here's what that means in practice:
                    </Typography>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        Identity and access
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    When you sign up for a Hubitable product, we ask for identifying information such as your name, email address, phone number, and maybe a company name. We then have to verify human identity as indicated in the terms of service, since bots are not allowed to sign up. Confirmation of identity also means you can personalize your new account, and we can send you relevant product updates and other essential information. We may also send you optional surveys from time to time to help us understand how you use our products and to make improvements. With your consent, we will send you our newsletter and other updates. We sometimes also give you the option to add a profile picture that displays in our products.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We'll never sell your personal information to third parties, and we won't use your name or company in marketing statements without your permission either.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        Billing information
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    If you sign up for a paid Hubitable product, you will be asked to provide your payment information and billing address. Credit card information is submitted directly to our payment processor and doesn't hit Hubitable servers. We store a record of the payment transaction, including the last 4 digits of the credit card number, for purposes of account history, invoicing, and billing support. We store your billing address so we can charge you for service, calculate any sales tax due, send you invoices, and detect fraudulent credit card transactions. We occasionally use aggregate billing information to guide our marketing efforts.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        Product interactions
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We store on our servers the content that you upload, receive, generate or maintain in your Hubitable product accounts. This is so you can use our products as intended. We keep this content as long as your account is active. If you delete your account, we'll delete the content within 60 days.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        General Geolocation data
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    For most of our products, we log the full IP address used to sign up a product account and retain that for use in mitigating future spammy signups. We also log all account access by full IP address for security and fraud prevention purposes, and we keep this login data for as long as your product account is active.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        Website interactions
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We collect information about your browsing activity for analytics and statistical purposes such as conversion rate testing and experimenting with new product designs. This includes, for example, your browser and operating system versions, your IP address, which web pages you visited and how long they took to load, and which website referred you to us. If you have an account and are signed in, these web analytics data are tied to your IP address and user account until your account is no longer active. The web analytics we use are described further in the Advertising and Cookies section.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        Anti-bot assessments
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We use CAPTCHA across our applications to mitigate brute force logins and as a means of spam protection. We have a legitimate interest in protecting our apps and the broader Internet community from credential stuffing attacks and spam. When you log into your Hubitable accounts and when you fill in certain forms, the CAPTCHA service evaluates various information (e.g., IP address, how long the visitor has been on the app, mouse movements) to try to detect if the activity is from an automated program instead of a human. The CAPTCHA service then provides Hubitable with the spam score results; we do not have access to the evaluated information.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        Advertising and Cookies
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Hubitable runs contextual ads on various third-party platforms such as Google. Users who click on one of our ads will be sent to the Hubitable marketing site. Where permissible under law, we may load an ad-company script on their browsers that sets a third-party cookie and sends information to the ad network to enable evaluation of the effectiveness of our ads, e.g., which ad they clicked and which keyword triggered the ad, and whether they performed certain actions such as clicking a button or submitting a form.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We also use persistent first-party cookies and some third-party cookies to store certain preferences, make it easier for you to use our applications, and perform A/B testing as well as support some analytics.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    A cookie is a piece of text stored by your browser. It may help remember login information and site preferences. It might also collect information such as your browser type, operating system, web pages visited, duration of visit, content viewed, and other click-stream data. You can adjust cookie retention settings and accept or block individual cookies in your browser settings, although our apps won't work and other aspects of our service may not function properly if you turn cookies off.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        Voluntary correspondence
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    When you email Hubitable with a question or to ask for help, we keep that correspondence, including your email address, so that we have a history of past correspondence to reference if you reach out in the future.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We also store information you may volunteer, for example, written responses to surveys. If you agree to a customer interview, we may ask for your permission to record the conversation for future reference or use. We will only do so with your express consent.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="h3" sx={{ mb: 2, color: 'text.primary' }}>
                        How we approach mobile app permissions
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We offer optional desktop (i.e. progressive web apps) and mobile apps for some of our products. Because of how the platforms are designed, our apps typically must request your consent before accessing contacts, calendar, camera, and other privacy-sensitive features of your device. Consent is always optional and our apps will function without it, though some features may be unavailable. There are a few exceptions, for example:
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    • Our iOS apps will ask for permission to use push notifications upon first sign-in.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    • Android apps do not require permission to send push notifications.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Legal basis for processing your data
                    </Typography>
                    <Typography level="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        Under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, we are required to explain the legal bases for processing your personal data. We rely on the following legal bases:
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Contract:</strong> When processing is necessary for the performance of a contract with you (e.g., providing our services).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Consent:</strong> When you have given us clear consent to process your personal data for a specific purpose (e.g., sending newsletters).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Legitimate Interests:</strong> When processing is necessary for our legitimate interests or those of a third party, provided your rights and interests do not override these interests (e.g., preventing fraud, improving our services).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Legal Obligation:</strong> When processing is necessary to comply with a legal obligation (e.g., responding to lawful requests from authorities).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        When we access or disclose your information
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>To provide products or services you've requested.</strong> We use some third-party subprocessors to help run our applications and provide the Services to you (e.g Google Sign-In/Sign-Up for authentication). We may also use third-party processors for other business functions such as managing newsletter subscriptions, sending customer surveys, and providing our company storefront.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>At your direction.</strong> We may disclose your information at your direction if you integrate a third-party service into your use of our products. For example, we may allow you, at your option, to connect your Google Calendar account to your Hubitable Athlete account so that you can use Hubitable Athlete to schedule your meals and workout activities. These details will be stored by both Hubitable and Google and will be available to you from your Google Calendar account as well as your Hubitable Athlete account.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Limited access by Hubitable employees.</strong> No Hubitable human looks at your content except for limited purposes with your express permission, for example, if an error occurs that stops an automated process from working and requires manual intervention to fix. These are rare cases, and when they happen, we look for root cause solutions as much as possible to avoid them recurring. We may also access your data if required in order to respond to legal process (see "When required under applicable law" below).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>To help you troubleshoot or squash a software bug.</strong> If at any point we need to access your content to help you with a support case, we will ask for your consent before proceeding.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>To investigate, prevent, or take action regarding restricted uses.</strong> Accessing a customer's account when investigating potential abuse is a measure of last resort. We want to protect the privacy and safety of both our customers and the people reporting issues to us, and we do our best to balance those responsibilities throughout the process. If we discover you are using our products for a restricted purpose, we will take action as necessary, including notifying appropriate authorities where warranted.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Aggregated and de-identified data.</strong> We may aggregate and/or de-identify information collected through the services. De-identifying data means removing information that can identify you as an individual or an organization. We may use de-identified or aggregated data for any purpose, including marketing or analytics.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>When required under applicable law.</strong> Hubitable Ltd is a UK company and all data infrastructure are located in the UK.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Your rights with respect to your information
                    </Typography>
                    <Typography level="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        At Hubitable, we strive to apply the same data rights to all customers, regardless of their location. Under the UK GDPR, you have the following rights:
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Right to be informed:</strong> You have the right to know how your personal data is being used, as detailed in this policy.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Right of access:</strong> You can request a copy of the personal data we hold about you.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Right to rectification:</strong> You can request that we correct any inaccuracies in your personal data.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Right to erasure:</strong> You can request that we delete your personal data, subject to certain conditions.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Right to restrict processing:</strong> You can request that we limit the way we use your personal data.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Right to data portability:</strong> You can request to receive your personal data in a structured, commonly used format and transmit it to another controller.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Right to object:</strong> You can object to the processing of your personal data in certain circumstances, such as for direct marketing.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Rights related to automated decision-making and profiling:</strong> You have the right not to be subject to decisions based solely on automated processing, including profiling, which produce legal effects or similarly significant impacts.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        Many of these rights can be exercised by signing in and updating your account information. Please note that certain information may be exempt from such requests under applicable law. For example, we need to retain certain information in order to provide our services to you.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        In some cases, we also need to take reasonable steps to verify your identity before responding to a request, which may include, at a minimum, depending on the sensitivity of the information you are requesting and the type of request you are making, verifying your name, phone number and email address. If we are unable to verify you, we may be unable to respond to your requests. If you have questions about exercising these rights or need assistance, please contact us at{' '}
                        <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                            info@hubitable.co.uk
                        </Link>
                        {' '}or at Hubitable Ltd, 86-90 Paul Street, London, Greater London, England, EC2A 4NE. If an authorized agent is corresponding on your behalf, we will need written consent with a signature from the account holder before proceeding.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        Depending on applicable law, you may have the right to appeal our decision to deny your request, if applicable. We will provide information about how to exercise that right in our response denying the request. You also have the right to lodge a complaint with the UK's Information Commissioner's Office (ICO). You can contact the ICO at{' '}
                        <Link href="https://ico.org.uk" sx={{ color: 'primary.500' }}>
                            ico.org.uk
                        </Link>
                        {' '}or by calling 0303 123 1113.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        How we secure your data
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    All data is encrypted via SSL/TLS when transmitted from our servers to your browser. The database backups are also encrypted. In addition, we go to great lengths to secure your data at rest.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    For some of our products, most data are not encrypted while they live in our database (since they need to be ready to send to you when you need them), where as with others, we go further by encrypting the database at-work. Every field containing personal data is encrypted with its own key. The disks storing the data keys are encrypted as well. Our servers decrypt the data to send it to you when you need it.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        What happens when you delete content in your product accounts
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    In many of our applications, we give you the option to trash content. Anything you trash in your product accounts while they are active will be kept in an accessible trash can for about 25 days (it varies a little by product). After that time, the trashed content cannot be accessed via the application and we are not able to retrieve it for you. The trashed content may remain on our active servers for another 30 days, and copies of the content may be held in backups of our application databases for up to another 30 days after that. Altogether, any content trashed in your product accounts should be purged from all of our systems and logs within 90 days.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    If you choose to cancel your account, your content will become immediately inaccessible and should be purged from our systems in full within 60 days. This applies both for cases when an account owner directly cancels and for auto-canceled accounts. Please refer to our{' '}
                                    <Link href="/terms/cancellation" sx={{ color: 'primary.500' }}>
                                        Cancellation policy
                                    </Link>
                                    {' '}for more details.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Data retention
                    </Typography>
                    <Typography level="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        We keep your information for the time necessary for the purposes for which it is processed. The length of time for which we retain information depends on the purposes for which we collected and use it and your choices, after which time we may delete and/or aggregate it, hence de-identifying it. We may also retain and use this information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements. Specific retention periods are as follows:
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Account information:</strong> Retained for as long as your account is active and for 60 days after cancellation.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Billing information:</strong> Retained for 7 years to comply with tax and accounting obligations.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Product interactions:</strong> Content is retained until you delete it or cancel your account, with a 60-day grace period for account cancellations.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Website interactions:</strong> Web analytics data are retained for up to 26 months.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Correspondence:</strong> Retained for as long as necessary to provide support or as required by law.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        We will periodically review retained data to ensure it is still necessary for the purposes outlined.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Location of site and data
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Our products and other web properties are operated in the United Kingdom. If you are located in the UK or elsewhere outside of the United Kingdom, please be aware that any information you provide to us will be transferred to and stored in the United Kingdom. By using our websites or Services and/or providing us with your personal information, you consent to this transfer.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        International data transfers
                    </Typography>
                    <Typography level="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        As a UK-based company, we transfer personal data from the user locations to the United Kingdom. To ensure that your personal data is protected in line with UK data protection laws, we rely on the following mechanisms:
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Standard Contractual Clauses (SCCs):</strong> We have adopted SCCs approved by the UK government to safeguard your data when transferred outside the UK.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Adequacy decisions:</strong> Where applicable, we rely on adequacy decisions by the UK government for data transfers to countries deemed to provide adequate protection.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        For occasional transfers, such as when you sign up for our newsletter or participate in surveys, we rely on derogations under Article 49 of the UK GDPR, such as explicit consent or the performance of a contract.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Children's privacy
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Our services are not directed to children under the age of 16, and we do not knowingly collect personal data from children under 16. If we become aware that we have collected personal data from a child under 16 without parental consent, we will take steps to delete such information. If you believe we have collected data from a child under 16, please contact us at{' '}
                                    <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                                        Hubitable Support
                                    </Link>.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Changes and questions
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        We may update this policy as needed to comply with relevant regulations and reflect any new practices. Have any questions, comments, or concerns about this privacy policy, your data, or your rights with respect to your information? Please get in touch by emailing us at{' '}
                        <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                            info@hubitable.co.uk
                        </Link>
                        {' '}and we'll be happy to try to answer them!
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Privacy; 